import { buildDispatchMsgItem } from "@/components/mobile/chat/mode/util";
import { useMateDispatchMessage } from "@/stores/mateDispatchMessage";
import { IMessageItem } from "@/types/component";
import { MateDispatchMessage } from "@/types/entity";
import { useDebounceFn, useScroll, useTimeoutPoll } from "@vueuse/core";
import {
  computed,
  ComputedRef,
  nextTick,
  onMounted,
  Ref,
  ref,
  watch,
} from "vue";
export const useMateDispatchMessageList = ({
  uuid,
  messageContainerRef,
  scrollRef,
  myMessage,
  isStreamReplying,
}: {
  uuid: string;
  messageContainerRef: Ref<HTMLElement | null>;
  scrollRef: Ref<HTMLDivElement | null>;
  myMessage: Ref<string> | ComputedRef<string>;
  streamMsg: Ref<string> | ComputedRef<string>;
  isStreamReplying: Ref<boolean> | ComputedRef<boolean>;
}) => {
  const dispatchMessageStore = useMateDispatchMessage();

  const messageList = computed<IMessageItem[]>(() => {
    const messageList: MateDispatchMessage[] =
      dispatchMessageStore.messageListByDispatchUuid(uuid);
    return [...messageList].reverse().map((item) => buildDispatchMsgItem(item));
  });

  const scrollLoading = ref(false);
  const finished = ref(false);
  const error = ref(false);
  const scrollLoad = async () => {
    if (!scrollRef.value) return;
    try {
      scrollLoading.value = true;
      const oldScrollTop = scrollRef.value.scrollTop;
      const { hasMore, count } =
        await dispatchMessageStore.fetchOlderMessageList(uuid);
      await nextTick(() => {
        let offset = 0;
        for (let i = 0; i < count; i++) {
          const item = messageContainerRef.value!.children[i];
          offset += item.clientHeight;
        }
        scrollRef.value!.scrollTop = offset + oldScrollTop;
      });
      finished.value = !hasMore;
      scrollLoading.value = false;
    } catch (e) {
      error.value = true;
      throw e;
    } finally {
      scrollLoading.value = false;
    }
  };

  const { y: chatScrollY, isScrolling } = useScroll(scrollRef, {
    behavior: "smooth",
    offset: { bottom: 20 },
  });

  function scrollToBottom(onlyIfArrivedBottom = false) {
    if (!scrollRef.value || isScrolling.value) return;
    const offsetHeight =
      scrollRef.value.scrollHeight - scrollRef.value.clientHeight;
    const isBottom = offsetHeight - chatScrollY.value < 200;
    if (onlyIfArrivedBottom && !isBottom) return;
    chatScrollY.value = scrollRef.value.scrollHeight;
  }

  const debounceScrollToBottom = useDebounceFn(scrollToBottom, 100);

  onMounted(() => {
    // 进入页面时，滚动到底部
    debounceScrollToBottom();
  });

  watch(myMessage, (myMessage) => myMessage && debounceScrollToBottom(), {
    flush: "post",
  });

  watch(isStreamReplying, (isStreamReplying) => {
    if (isStreamReplying) {
      nextTick(() => debounceScrollToBottom(true));
    }
  });

  useTimeoutPoll(
    async () => {
      if (dispatchMessageStore.fetchingFlag) return;
      const count = await dispatchMessageStore.getNewestMessage(
        uuid,
        true,
        () => {
          debounceScrollToBottom();
        }
      );
      if (count > 0) {
        debounceScrollToBottom();
      }
    },
    5000,
    { immediate: true }
  );

  return {
    messageList,
    scrollLoading,
    finished,
    error,
    scrollLoad,
    scrollToBottom,
    debounceScrollToBottom,
  };
};
